if (!customElements.get('natunora-offer-info')) {
  customElements.define('natunora-offer-info', class extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      if (this.closest('.natunora-offer--joints')) this.setupDisclosures(options);
      const tabs = [...this.querySelectorAll('[role=tab]')];
      const panels = [...this.querySelectorAll('[role=tabpanel]')];
      const steps = [...this.querySelectorAll('.n-info-step')];
      const timeline = this.querySelector('.n-info-timeline');
      const track = this.querySelector('.n-info-track');
      if (!tabs.length || !steps.length) return;
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      let measureNeeded = true, geometry = null;
      let shown = null, target = 0, previousTime = 0;
      const clamp = value => Math.max(0, Math.min(1, value));
      const paint = now => {
        this.frame = 0;
        if (panels[1].hidden || document.hidden || !this.isConnected) {
          previousTime = 0;
          return;
        }
        if (measureNeeded) {
          const nodes = steps.map(step => step.querySelector('.n-info-node').getBoundingClientRect());
          const origin = nodes[0].top + nodes[0].height / 2;
          const centers = nodes.map(node => node.top + node.height / 2 - origin);
          const length = centers.at(-1);
          geometry = { centers, length, radius: nodes[0].height / 2 };
          const atPageEnd = scrollY > 0 && scrollY + innerHeight >= document.documentElement.scrollHeight - 2;
          target = motion.matches || atPageEnd ? length + geometry.radius : Math.max(-geometry.radius, Math.min(length + geometry.radius, innerHeight * .7 - origin));
          const top = origin - timeline.getBoundingClientRect().top;
          track.style.height = `${length}px`;
          track.style.top = `${top}px`;
          measureNeeded = false;
        }
        if (shown === null) shown = -geometry.radius;
        if (motion.matches) shown = target;
        const elapsed = previousTime ? Math.min(64, now - previousTime) : 16.67;
        previousTime = now;
        shown += (target - shown) * (1 - Math.exp(-elapsed / 65));
        if (Math.abs(target - shown) < .1) shown = target;
        track.style.setProperty('--progress', geometry.length > 0 ? clamp(shown / geometry.length) : 1);
        steps.forEach((step, index) => {
          const progress = clamp((shown - geometry.centers[index] + geometry.radius) / (geometry.radius * 2));
          step.style.setProperty('--mark-hidden', `${(1 - progress) * 100}%`);
          step.style.setProperty('--branch-progress', clamp((shown - geometry.centers[index] + 8) / 16));
          step.classList.toggle('is-active', progress >= .5);
        });
        if (shown !== target) this.frame = requestAnimationFrame(paint);
        else previousTime = 0;
      };
      const schedule = () => {
        measureNeeded = true;
        if (!this.frame) this.frame = requestAnimationFrame(paint);
      };
      const activate = index => {
        if (index === 1) {
          shown = null;
          previousTime = 0;
          track.style.setProperty('--progress', 0);
          steps.forEach(step => {
            step.style.setProperty('--mark-hidden', '100%');
            step.style.setProperty('--branch-progress', 0);
            step.classList.remove('is-active');
          });
        }
        tabs.forEach((tab, i) => {
          tab.setAttribute('aria-selected', String(i === index));
          tab.tabIndex = i === index ? 0 : -1;
          panels[i].hidden = i !== index;
        });
        this.querySelector('.n-info-tabs').style.setProperty('--tab-index', index);
        schedule();
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => activate(i), options);
        tab.addEventListener('keydown', event => {
          const next = { ArrowRight: (i + 1) % tabs.length, ArrowLeft: (i - 1 + tabs.length) % tabs.length, Home: 0, End: tabs.length - 1 }[event.key];
          if (next === undefined) return;
          event.preventDefault();
          activate(next);
          tabs[next].focus();
        }, options);
      });
      const questions = [...this.querySelectorAll('.n-info-question')];
      const setQuestion = (button, open) => {
        const answer = button.closest('.n-info-faq').querySelector('.n-info-answer');
        button.setAttribute('aria-expanded', String(open));
        answer.classList.toggle('is-open', open);
        answer.inert = !open;
      };
      questions.forEach(button => {
        setQuestion(button, false);
        button.closest('.n-info-faq').querySelector('.n-info-answer').hidden = false;
        button.addEventListener('click', () => {
          const open = button.getAttribute('aria-expanded') !== 'true';
          questions.forEach(question => setQuestion(question, question === button && open));
        }, options);
      });
      this.classList.add('is-ready');
      this.querySelector('.n-info-tabs').hidden = false;
      activate(0);
      window.addEventListener('scroll', schedule, { ...options, passive: true });
      window.addEventListener('resize', schedule, options);
      motion.addEventListener('change', schedule, options);
      document.addEventListener('visibilitychange', schedule, options);
      window.addEventListener('pagehide', () => { cancelAnimationFrame(this.frame); this.frame = null; }, options);
      window.addEventListener('pageshow', schedule, options);
      this.observer = new ResizeObserver(schedule);
      this.observer.observe(timeline);
      document.fonts.ready.then(() => { if (!options.signal.aborted) schedule(); });
    }
    setupDisclosures(options) {
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      const items = [...this.querySelectorAll('details')];
      const states = new Map();
      const settle = (detail, state) => {
        state.animation?.cancel();
        state.animation = null;
        detail.open = state.expanded;
        detail.style.removeProperty('overflow');
      };
      const toggle = (detail, expanded) => {
        const state = states.get(detail);
        const start = detail.getBoundingClientRect().height;
        state.animation?.cancel();
        state.animation = null;
        state.expanded = expanded;
        state.summary.setAttribute('aria-expanded', String(expanded));
        const css = getComputedStyle(detail);
        const time = css.getPropertyValue('--natunora-motion-duration').trim();
        const duration = motion.matches ? 0 : parseFloat(time) * (time.endsWith('ms') ? 1 : 1000);
        if (!duration || !detail.animate) {
          settle(detail, state);
          return;
        }
        detail.open = true;
        const end = expanded ? detail.getBoundingClientRect().height :
          state.summary.getBoundingClientRect().height + parseFloat(css.borderTopWidth) +
          parseFloat(css.borderBottomWidth) + parseFloat(css.paddingTop) + parseFloat(css.paddingBottom);
        detail.style.overflow = 'hidden';
        const animation = detail.animate([{ height: `${start}px` }, { height: `${end}px` }], {
          duration, easing: css.getPropertyValue('--natunora-motion-easing').trim(), fill: 'both'
        });
        state.animation = animation;
        animation.onfinish = () => {
          if (state.animation === animation) settle(detail, state);
        };
      };
      items.forEach(detail => {
        const summary = detail.querySelector(':scope > summary');
        const state = { expanded: detail.open, summary, animation: null, name: detail.getAttribute('name') };
        states.set(detail, state);
        detail.removeAttribute('name');
        summary.setAttribute('aria-expanded', String(detail.open));
        summary.addEventListener('pointerdown', () => summary.setAttribute('data-pointer-focus', ''), options);
        summary.addEventListener('keydown', () => summary.removeAttribute('data-pointer-focus'), options);
        summary.addEventListener('blur', () => summary.removeAttribute('data-pointer-focus'), options);
        summary.addEventListener('click', event => {
          event.preventDefault();
          const expanded = !state.expanded;
          if (detail.matches('.n-info-faq') && expanded) {
            states.forEach((other, peer) => {
              if (peer !== detail && peer.matches('.n-info-faq') && other.expanded) toggle(peer, false);
            });
          }
          const parent = detail.parentElement.closest('details');
          if (parent && states.has(parent) && states.get(parent).animation) settle(parent, states.get(parent));
          states.forEach((child, node) => {
            if (node !== detail && detail.contains(node) && child.animation) settle(node, child);
          });
          toggle(detail, expanded);
        }, options);
      });
      const finish = () => states.forEach((state, detail) => settle(detail, state));
      window.addEventListener('resize', finish, options);
      motion.addEventListener('change', finish, options);
      this.cleanupDisclosures = () => {
        finish();
        states.forEach((state, detail) => {
          if (state.name) detail.setAttribute('name', state.name);
          state.summary.removeAttribute('aria-expanded');
          state.summary.removeAttribute('data-pointer-focus');
        });
      };
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.cleanupDisclosures?.();
      this.cleanupDisclosures = null;
      this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  });
}
